import React from 'react';
import { Box, Typography, Grid, Button, Menu, MenuItem, IconButton } from '@mui/material';
import { Match } from '../types';
import { t } from 'i18next';
import trophy from 'src/assets/images/tournaments/Trophy.png';

import './matchCard.css';
import { IconDotsVertical, IconEdit, IconMenu } from '@tabler/icons';
import { ScheduleMatch } from 'src/services/tournaments/getTournamentSchedule';
import {
  useResetMatchScoreMutation,
  useSwapMatchTeamsNamesMutation,
} from 'src/services/tournaments';
import useToast from 'src/hooks/useToast';
import { useParams } from 'react-router';
import SwapTeamsForm from './SwapTeamsForm';
import SeedMatch from './SeedMatch';

const scoreProps = {
  bgcolor: 'white',
  item: true,
  xs: 0.9,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: 0.5,
  mx: 0.1,
  maxHeight: 23,
  mt: 1.5,
};

const MatchCard = ({
  match,
  setSelectedMatch,
  setOpenEditMatch,
  loadMatches,
}: {
  match: Match;
  setSelectedMatch: React.Dispatch<React.SetStateAction<ScheduleMatch>>;
  setOpenEditMatch: React.Dispatch<React.SetStateAction<boolean>>;
  loadMatches: () => Promise<void>;
}) => {
  const { id } = useParams();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [swapMatchTeamsNames] = useSwapMatchTeamsNamesMutation();
  const [resetScore] = useResetMatchScoreMutation();

  const [openSwapTeamsForm, setOpenSwapTeamsForm] = React.useState(false);
  const [openSeedMatchForm, setOpenSeedMatchForm] = React.useState(false);

  const { toastPromise } = useToast();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const updateMatch = () => {
    setSelectedMatch(match as unknown as ScheduleMatch);
    setOpenEditMatch(true);
    handleClose();
  };

  const swapTeamNames = () => {
    handleClose();
    toastPromise({
      request: () =>
        swapMatchTeamsNames({
          tournamentId: Number(id),
          matchId: Number(match.id),
        }),
      onSuccess: (result) => {
        if ('error' in result) {
          throw result.error;
        }

        loadMatches();

        return t('TournamentScheduleTab.teamNamesSwappedSuccessfully');
      },
      onError: (err) => {
        if (err.data && err.data.errors) {
          const errorMessages = Object.values(err.data.errors).flat().join('. ');

          return errorMessages;
        }
        if (err?.data?.message) {
          return err?.data?.message;
        }

        return t('TournamentScheduleTab.teamNamesSwapFailed');
      },
    });
  };

  const handleResetScore = () => {
    toastPromise({
      request: () =>
        resetScore({
          tournamentId: Number(id),
          matchId: Number(match?.id),
        }),
      onSuccess: (result) => {
        if ('error' in result) {
          throw result.error;
        }

        loadMatches();

        return t('TournamentScheduleTab.scoreResetSuccessfully');
      },
      onError: (err) => {
        if (err.data && err.data.errors) {
          const errorMessages = Object.values(err.data.errors).flat().join('. ');

          return errorMessages;
        }
        if (err?.data?.message) {
          return err?.data?.message;
        }

        return t('TournamentScheduleTab.scoreResetFailed');
      },
    });
  };

  const handleSwapTeams = () => {
    setOpenSwapTeamsForm(true);
    handleClose();
  };

  const handleSeedMatch = () => {
    setOpenSeedMatchForm(true);
    handleClose();
  };

  const team1Result = match?.teams_results?.map((result) => result.team_1);

  const team2Result = match?.teams_results?.map((result) => result.team_2);

  return (
    <>
      <Box
        sx={{
          maxWidth: 550,
          backgroundColor: '#DFEEFF',
        }}
        height={100}
        overflow={'visible'}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: -20,
            right: -10,
            zIndex: 10,
            p: 0,
            minWidth: 0,
            px: 1.5,
          }}
          aria-controls={open ? 'edit-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          id="edit-button"
        >
          <IconDotsVertical size={16} />
        </IconButton>
        <Menu
          id="edit-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'edit-button',
          }}
        >
          <MenuItem onClick={updateMatch}>{t('TournamentScheduleTab.update')}</MenuItem>
          <MenuItem onClick={handleResetScore}>{t('TournamentScheduleTab.resetScore')}</MenuItem>
          {(match?.participants?.[0]?.id || match?.participants?.[1]?.id) && (
            <MenuItem onClick={handleSwapTeams}>{t('TournamentScheduleTab.swap')}</MenuItem>
          )}
          <MenuItem onClick={swapTeamNames}>{t('TournamentScheduleTab.swapTeamNames')}</MenuItem>
          {match.round_num === 1 && (
            <MenuItem onClick={handleSeedMatch}>{t('TournamentScheduleTab.seed')}</MenuItem>
          )}
        </Menu>
        <Grid container>
          <Grid bgcolor={'#000E41'} item xs={1} height={50} display="flex" justifyContent="center">
            <Typography
              fontSize={8}
              color={'white'}
              variant="h6"
              my={1}
              sx={{
                textOrientation: 'sideways',
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              {t('TournamentScheduleTab.teamA')}
            </Typography>
          </Grid>
          <Grid item xs={7} display="flex" flexDirection="column" justifyContent="center">
            <Typography color={'#4392F1'} sx={{ ml: '5px' }} variant="h6" fontSize={10}>
              {match?.participants?.[0]?.name || '-'}
            </Typography>
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="center" alignItems="center">
            {match?.winner_team && match?.winner_team === match?.tournament_team_1_id && (
              <img className="match-card-winner-icon" src={trophy} alt="winner" />
            )}
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team1Result?.[0] || '-'}</Typography>
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team1Result?.[1] || '-'}</Typography>
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team1Result?.[2] || '-'}</Typography>
          </Grid>

          <Grid bgcolor={'#000E41'} item xs={1} height={50} display="flex" justifyContent="center">
            <Typography
              fontSize={8}
              color={'white'}
              variant="h6"
              my={1}
              sx={{
                textOrientation: 'sideways',
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              {t('TournamentScheduleTab.teamB')}
            </Typography>
          </Grid>
          <Grid item xs={7} display="flex" flexDirection="column" justifyContent="center">
            <Typography color={'#4392F1'} sx={{ ml: '5px' }} variant="h6" fontSize={10}>
              {match?.participants?.[1]?.name || '-'}
            </Typography>
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="center" alignItems="center">
            {match?.winner_team && match?.winner_team === match?.tournament_team_2_id && (
              <img className="match-card-winner-icon" src={trophy} alt="winner" />
            )}
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team2Result?.[0] || '-'}</Typography>
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team2Result?.[1] || '-'}</Typography>
          </Grid>
          <Grid {...scoreProps}>
            <Typography fontSize={10}>{team2Result?.[2] || '-'}</Typography>
          </Grid>
        </Grid>
        <div className="match-card-line" />
      </Box>
      <SwapTeamsForm
        open={openSwapTeamsForm}
        setOpen={setOpenSwapTeamsForm}
        match={match}
        loadMatches={loadMatches}
      />
      <SeedMatch
        open={openSeedMatchForm}
        setOpen={setOpenSeedMatchForm}
        match={match}
        loadMatches={loadMatches}
      />
    </>
  );
};

export default MatchCard;
